import { CustomDatePicker } from '@components/controls/CustomDatePicker';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select, { SelectOption } from '@amzn/meridian/select';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import { useAppSelector } from 'src/store/hooks';
import { selectProgram } from 'src/store/slices/programSlice';
import AgileURLParameterTypes from '@components/agile/types/AgileURLParameterTypes';
import { useSearchParams } from 'react-router-dom';
import Heading from '@amzn/meridian/heading';
import { escapeRegExp } from 'lodash';
import Text from '@amzn/meridian/text';
import useGetMacroplanogramResults from 'src/store/hooks/useGetMacroplanogramResults';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import usePublishZone from 'src/store/hooks/usePublishResolver';
import usePutZoneMapConfig from 'src/store/hooks/usePutZoneMapConfig';
import ZoneCreationComponent from '@components/agile/agileComponents/ZoneCreationComponent';
import Divider from '@amzn/meridian/divider';
import NavigationHelper from '@components/agile/utils/URLParametersUtils';
import Modal from '@amzn/meridian/modal';
import useGetCategory from 'src/store/hooks/useGetCategory';
import useListFloorplans from 'src/store/hooks/useListFloorplans';
import Loader from '@amzn/meridian/loader';
import Toaster from '@amzn/meridian/toaster';
import { ToasterToasts } from '@amzn/meridian/toaster/toaster';
import {
  createMissingFixtureDataToast,
  createNoFixtureToast,
  createFixtureAlreadyAssignedToast,
  createNoCategoryToast,
} from '@components/agile/utils/ToasterUtils';
import ViewZoneComponent from './zoneViewer/ViewZoneComponent';
import ZoneViewerModalComponent from './zoneViewer/ZoneViewerModalComponent';
import { SwitchButtonValue, ZoneCreationProperties } from './types/zoneMapType';
import { colorByKey } from './zoneViewer/zoneMapColorHelper';
import {
  buildFixtureIdToEditingZoneDataMap,
  filterZoneCreationProperties,
  generateZoneNameKeyMap,
} from './zoneViewer/zoneMapDataHelper';
import { ProductGroupOption } from './types/ProductTaxonomyTypes';
import StoreOrModeChangeConfirmationModal from './zoneViewer/StoreOrModeChangeConfirmationModal';
import ZoneCreationMenu from './zoneViewer/zoneCreationMenu';
import useFeatureFlag from 'src/hooks/useFeatureFlag';
import FeatureName from '@constants/featureName';
import useGetProductGroups from 'src/store/hooks/useGetProductGroups';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import { useFixtureSelection } from './hooks/useFixtureSelection';
import { usePickPathDrawing } from './hooks/usePickPathDrawing';
import { useViewModeData } from './hooks/useViewModeData';
import { useViewPickPathEditing } from './hooks/useViewPickPathEditing';
import { useSequenceDrawing } from './hooks/useSequenceDrawing';
import { useCreateZoneState } from './hooks/useCreateZoneState';
import { useFloorplanData } from './hooks/useFloorplanData';
import { usePublishZoneMap } from './hooks/usePublishZoneMap';
import { buildCopyToNewProperties } from './hooks/buildCopyToNewProperties';
import useUpdateFloorplan from 'src/store/hooks/useUpdateFloorplan';
import { validateAllocations } from './allocation/validateAllocations';
import MultiStoreCopy from './multiStoreCopy/MultiStoreCopy';

export interface ZoneViewerProps {
  urlParams: AgileURLParameterTypes;
  setUrlParameter: (value: AgileURLParameterTypes) => void;
  switchButtonValue: string | undefined;
  setSwitchButtonValue: (value: SwitchButtonValue | undefined) => void;
}

const ZoneViewer = (props: ZoneViewerProps) => {
  const { urlParams, setUrlParameter, switchButtonValue, setSwitchButtonValue } = props;
  const programType = useAppSelector(selectProgram);
  const config = useProgramConfig();
  const isGfcPublish = config.macroplanogramScope === 'category';
  const floorplanPickerEnabled = Boolean(config.features.zoneMap?.floorplanPicker);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const [newSwitchButtonValue, setNewSwitchButtonValue] = useState<SwitchButtonValue | undefined>(undefined);
  const [isLoadingPublishZone, setIsLoadingPublishZone] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [store, setStore] = useState(urlParams.storeId);
  const [date, setDate] = useState(urlParams.date || new Date().toISOString().split('T')[0]);
  const [autoSelectedStore, setAutoSelectedStore] = useState<string | undefined>(
    urlParams.date ? urlParams.storeId : undefined,
  );
  const { floorplans: storeFloorplans, isLoading: isListFloorplansLoading } = useListFloorplans({
    storeId: store,
    enabled: floorplanPickerEnabled,
  });
  useEffect(() => {
    if (!floorplanPickerEnabled) return;
    if (!store || autoSelectedStore === store) return;
    if (storeFloorplans.length === 0) return;
    const target = storeFloorplans.find((fp) => fp.isLive) ?? storeFloorplans[0];
    if (target?.goLiveDate) {
      setDate(target.goLiveDate.split('T')[0]);
    }
    setAutoSelectedStore(store);
  }, [floorplanPickerEnabled, store, storeFloorplans, autoSelectedStore]);
  const [selectedZoneId] = useState(urlParams.zoneId);
  const [publishZoneSuccessful, setPublishZoneSuccessful] = useState(false);
  const [productGroupFilter, setProductGroupFilter] = useState('');
  const [selectedProductGroup, setSelectedProductGroup] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [publishZoneErrorMsg, setPublishZoneErrorMsg] = useState<string | undefined>(undefined);
  const [macroplanogramNameInput, setMacroplanogramNameInput] = useState('');
  const [selectedFixtures, setSelectedFixtures] = useState<string[]>([]);
  const [reassignmentConfirmation, setReassignmentConfirmation] = useState<{
    open: boolean;
    fixtureId: string;
    sourceZoneName: string;
    targetZoneName: string;
    sourceZoneIdx: number;
  } | null>(null);
  const [toasts, setToasts] = useState<ToasterToasts[]>([]);
  const onCloseToast = useCallback((id: string) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);
  const openNoFixtureToaster = useCallback(() => setToasts((prev) => prev.concat(createNoFixtureToast())), []);
  const openMissingDataToaster = useCallback((fixtureCount: number) => {
    setToasts((prev) => prev.concat(createMissingFixtureDataToast(fixtureCount)));
  }, []);

  // Auto-dismiss toasts after their dismissAfter time
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    toasts.forEach((toast) => {
      const dismissAfter = (toast as { dismissAfter?: number }).dismissAfter;
      if (dismissAfter) {
        const timer = setTimeout(() => {
          onCloseToast(toast.id);
        }, dismissAfter);
        timers.push(timer);
      }
    });

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [toasts, onCloseToast]);
  const timeStamp = new Date(date).getTime();
  const nextGenEnabled = useFeatureFlag(FeatureName.NEXT_GEN_V2);
  const { mutateAsync: publishZone } = usePublishZone();
  const { mutateAsync: putZoneMapConfig } = usePutZoneMapConfig();
  const { mutateAsync: updateFloorplanPickPath } = useUpdateFloorplan();
  const [copyToNewDate, setCopyToNewDate] = useState(urlParams.date);
  const copyToNewTimestamp = new Date(copyToNewDate).getTime();
  const [newStore, setNewStore] = useState<string | undefined>(undefined);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [storeOrModeChangeConfirmationModal, setStoreOrModeChangeConfirmationModal] = useState<{
    open: boolean;
    showStoreWarning: boolean;
  }>({ open: false, showStoreWarning: false });
  const {
    zoneNameList,
    macroplanogramList,
    isSuccess: isGetZoneDataSuccess,
  } = useGetMacroplanogramResults({
    storeId: store,
    timestamp: timeStamp,
    includeFuture: false,
    includeDraft: false,
  });
  const {
    fixtures,
    fpData,
    filteredFpData,
    agileFixtures,
    availableDepartments,
    fixtureOrderingMap,
    floorplanList,
    isGetFloorplanLoading,
    isGetFloorplanSuccess,
  } = useFloorplanData({
    store,
    timeStamp,
    copyToNewTimestamp,
    switchButtonValue,
    selectedDepartment,
  });

  const {
    fixtureIdToZoneDataMap: fixtureIdToZoneDataMapForView,
    viewPickPathLayers,
    viewAllocations,
    viewFixtureAssignments,
  } = useViewModeData({
    zoneNameList,
    macroplanogramList,
    selectedDepartment,
    agileFixtures,
    fixtureOrderingMap,
    fpData,
  });

  // GFC view-mode pick-path editing (toolbar): edit the saved flow without
  // republishing. Persists per-fixture pickPathId via updateFloorplan.
  const viewFloorplanId = floorplanList.length > 0 ? (floorplanList[0].floorplanId ?? undefined) : undefined;
  const pickPathEditing = useViewPickPathEditing({
    savedFixtureOrderingMap: fixtureOrderingMap,
    floorplanData: filteredFpData,
    store,
    floorplanId: viewFloorplanId,
    updateFloorplanPickPath: updateFloorplanPickPath as unknown as (input: { input: unknown }) => Promise<unknown>,
    setToasts: setToasts as React.Dispatch<React.SetStateAction<unknown[]>>,
    enabled: isGfcPublish,
  });

  const { productGroupList } = useGetProductGroups();

  useEffect(() => {
    if (
      (date !== '' &&
        store !== '' &&
        switchButtonValue !== undefined &&
        switchButtonValue !== SwitchButtonValue.CopyToNewMap) ||
      (copyToNewDate !== '' && store !== '' && switchButtonValue === SwitchButtonValue.CopyToNewMap)
    ) {
      if (!isGetFloorplanLoading && isGetFloorplanSuccess && fixtures.length === 0) {
        openNoFixtureToaster();
        console.warn('No fixtures found.');
      }
      const fixturesMissingAttributes = fixtures.filter(
        (fixture) =>
          fixture.height == null ||
          fixture.width == null ||
          fixture.depth == null ||
          fixture.rotation == null ||
          fixture.translation == null,
      );
      if (fixturesMissingAttributes.length > 0) {
        openMissingDataToaster(fixturesMissingAttributes.length);
        console.warn(
          `Found ${fixturesMissingAttributes.length} fixtures missing dimensions or pos data.`,
          fixturesMissingAttributes,
        );
      }
    }
  }, [floorplanList, switchButtonValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const createZoneState = useCreateZoneState({
    agileFixtures,
    editingFixtureOrderingMap: new Map(), // Will be connected after sequenceDrawing is initialized
    fixtureOrderingMap,
    switchButtonValue,
    copyToNewDate,
  });
  const {
    zoneCreationProperties,
    setZoneCreationProperties,
    zoneCreationValidationErrors,
    setZoneCreationValidationErrors,
    activeZoneIdx,
    setActiveZoneIdx,
    initialZoneCreationPropertiesMap,
    updateProductGroup,
    updateCategoryId,
    updateSubcategories,
    updateAllocations,
    handleZoneCreationSubmit,
  } = createZoneState;

  // Integrate fixture selection hook for visual selection
  const isSelectionEnabled =
    switchButtonValue === SwitchButtonValue.CreateZoneMap || switchButtonValue === SwitchButtonValue.CopyToNewMap;

  const fixtureSelection = useFixtureSelection({
    zoneCreationProperties,
    setZoneCreationProperties,
    selectedFixtures,
    setSelectedFixtures,
    agileFixtures,
    activeZoneIdx,
  });

  const pickPathDrawing = usePickPathDrawing({
    zoneCreationProperties,
    setZoneCreationProperties,
    activeZoneIdx,
    fixtureData: fpData,
  });

  const sequenceDrawing = useSequenceDrawing({
    zoneCreationProperties,
    activeZoneIdx,
    pickPathDrawing,
    fixtureOrderingMap,
    agileFixtures,
    store,
    setToasts: setToasts as React.Dispatch<React.SetStateAction<unknown[]>>,
    updateFloorplanPickPath: updateFloorplanPickPath as unknown as (input: { input: unknown }) => Promise<unknown>,
    floorplanId: viewFloorplanId,
  });
  const {
    editingFixtureOrderingMap,
    displayFixtureOrderingMap,
    fixtureAssignments,
    isSubmittingSequence,
    handleSubmitSequence,
    hasPickPaths,
    startNumber: createStartNumber,
    stepIncrement: createStepIncrement,
    updateStartNumber: updateCreateStartNumber,
    updateStepIncrement: updateCreateStepIncrement,
  } = sequenceDrawing;

  // Wrapper functions to handle selection results and show toasts
  // GFC guard: block fixture assignment until the active card has a category picked.
  const guardNoCategorySelected = useCallback((): boolean => {
    if (!isGfcPublish) return false;
    const activeZone = zoneCreationProperties.get(activeZoneIdx);
    if (!activeZone?.categoryId) {
      setToasts((prev) => [...prev, createNoCategoryToast(config.labels.category)]);
      return true;
    }
    return false;
  }, [isGfcPublish, zoneCreationProperties, activeZoneIdx, config.labels.category, setToasts]);

  const handleFixtureClickWithFeedback = useCallback(
    (fixtureId: string, modifierKeys: { shift?: boolean; ctrl?: boolean; meta?: boolean }) => {
      const normalizedModifierKeys = {
        shift: modifierKeys.shift ?? false,
        ctrl: modifierKeys.ctrl ?? false,
        meta: modifierKeys.meta ?? false,
      };
      if (guardNoCategorySelected()) return;

      // For GFC publish path, show reassignment confirmation modal
      // instead of just a toast when fixture is already assigned to another zone
      if (isGfcPublish) {
        const assignedZone = fixtureSelection.getFixtureAssignedZone(fixtureId);
        if (assignedZone) {
          // Find the source zone index
          let sourceZoneIdx = -1;
          for (const [zoneIdx, zone] of zoneCreationProperties.entries()) {
            if (zoneIdx !== activeZoneIdx && zone.fixtureIds.includes(fixtureId)) {
              sourceZoneIdx = zoneIdx;
              break;
            }
          }
          const targetZoneName = zoneCreationProperties.get(activeZoneIdx)?.zoneName || `Zone ${activeZoneIdx + 1}`;
          setReassignmentConfirmation({
            open: true,
            fixtureId,
            sourceZoneName: assignedZone,
            targetZoneName,
            sourceZoneIdx,
          });
          return;
        }
      }

      const result = fixtureSelection.handleFixtureClick(fixtureId, normalizedModifierKeys);
      if (result && !result.success && result.error) {
        const assignedZone = fixtureSelection.getFixtureAssignedZone(fixtureId);
        if (assignedZone) {
          setToasts((prev) => [...prev, createFixtureAlreadyAssignedToast(fixtureId, assignedZone)]);
        }
      }
    },
    [fixtureSelection, setToasts, isGfcPublish, zoneCreationProperties, activeZoneIdx, guardNoCategorySelected],
  );

  // Reassignment confirmation handlers for GFC direct allocation mode
  const handleConfirmReassignment = useCallback(() => {
    if (!reassignmentConfirmation) return;
    const { fixtureId, sourceZoneIdx } = reassignmentConfirmation;
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      // Remove from source zone
      const sourceZone = newMap.get(sourceZoneIdx);
      if (sourceZone) {
        newMap.set(sourceZoneIdx, {
          ...sourceZone,
          fixtureIds: sourceZone.fixtureIds.filter((id) => id !== fixtureId),
        });
      }
      // Add to target zone (active zone)
      const targetZone = newMap.get(activeZoneIdx);
      if (targetZone) {
        newMap.set(activeZoneIdx, {
          ...targetZone,
          fixtureIds: [...targetZone.fixtureIds, fixtureId],
        });
      }
      return newMap;
    });
    setReassignmentConfirmation(null);
  }, [reassignmentConfirmation, activeZoneIdx, setZoneCreationProperties]);

  const handleCancelReassignment = useCallback(() => {
    setReassignmentConfirmation(null);
  }, []);

  // Shift+drag marquee assigns to the active Drop Zone, matching single-click
  // assignment. Fixtures already assigned (to this or another zone) are ignored
  // by the hook.
  const handleMarqueeAssign = useCallback(
    (fixtureIds: string[]) => {
      if (guardNoCategorySelected()) return;
      fixtureSelection.assignFixturesToActiveZone(fixtureIds);
    },
    [fixtureSelection, guardNoCategorySelected],
  );

  useEffect(() => {
    if (switchButtonValue === SwitchButtonValue.CreateZoneMap && store && date && isGetFloorplanSuccess) {
      const filteredZoneCreationProperties = filterZoneCreationProperties(zoneCreationProperties, agileFixtures);
      const selectedFixturesAfterFiltering = Array.from(filteredZoneCreationProperties.values()).flatMap(
        (zone) => zone.fixtureIds,
      );
      setSelectedFixtures(selectedFixturesAfterFiltering);
      setZoneCreationProperties(filteredZoneCreationProperties);
    }
  }, [date, isGetFloorplanSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const zoneCreationPropertiesValues = Array.from(zoneCreationProperties.values());
  // generate zoneNameKeyMap for zone data from side bar
  const zoneNameKeyMapForEditingZone = generateZoneNameKeyMap(zoneCreationPropertiesValues);
  const fixtureIdToEditingZoneDataMap = useMemo(
    () => buildFixtureIdToEditingZoneDataMap(zoneCreationProperties, fpData, zoneNameKeyMapForEditingZone),
    [zoneCreationProperties, fpData, zoneNameKeyMapForEditingZone],
  );

  // GFC: per-Drop-Zone legend entries shown on the floorplan during fixture
  // assignment. Color matches the FIXTURE_ID display mode (colorByKey of the
  // card's categoryId), so the swatch in the legend equals the fill on the
  // floor and the same color shows up again in view mode.
  const dropZoneLegendEntries = useMemo(() => {
    if (!isGfcPublish) return undefined;
    const zones = Array.from(zoneCreationProperties.values()).filter((z) => !!z.categoryId);
    return zones.map((zone) => ({
      name: zone.zoneName || zone.categoryId,
      color: colorByKey(zone.categoryId),
      fixtureCount: zone.fixtureIds.length,
    }));
  }, [isGfcPublish, zoneCreationProperties]);

  if (programType == null) {
    throw new Error('ProgramType is not initialized');
  }

  const productTaxonomy = useGetCategory({
    productGroup: selectedProductGroup,
  });

  const { onPublishButton } = usePublishZoneMap({
    zoneCreationProperties,
    fixtureAssignments,
    selectedDepartment,
    selectedProductGroup,
    selectedCategory,
    macroplanogramNameInput,
    store,
    timeStamp,
    copyToNewTimestamp,
    switchButtonValue,
    nextGenEnabled,
    productTaxonomy,
    publishZone: publishZone as unknown as (input: {
      input: unknown;
    }) => Promise<{ publishZone: { error: { errorMessage: string } | null } }>,
    putZoneMapConfig: putZoneMapConfig as unknown as (input: {
      input: unknown;
    }) => Promise<{ putZoneMapConfig: { zoneMapConfigId: string | null } }>,
    initialZoneCreationPropertiesMap,
    setIsLoadingPublishZone,
    setPublishZoneSuccessful,
    setPublishZoneErrorMsg,
    setOpenConfirmationModal,
    setZoneCreationProperties,
  });

  const handleProductGroupChange = (productGroup: string) => {
    setSelectedProductGroup(productGroup);
    zoneCreationProperties.forEach((_, zoneIdx) => {
      updateProductGroup(zoneIdx, productGroup);
      updateCategoryId(zoneIdx, undefined);
      updateSubcategories(zoneIdx, [], []);
    });
    setSelectedCategory('');
  };
  const productGroupFilterRegExp = new RegExp(escapeRegExp(productGroupFilter), 'i');
  const programProductGroupOptions: ProductGroupOption[] = productGroupList.map((group) => ({
    label: group.name,
    value: group.id,
  }));
  const filteredProductGroupOptions = programProductGroupOptions.filter(
    (option: ProductGroupOption) => !productGroupFilter || productGroupFilterRegExp.test(option.label),
  );

  const categoryFilterRegExp = new RegExp(escapeRegExp(categoryFilter), 'i');
  const filteredCategoryOptions = productTaxonomy?.categoryList?.filter(
    (option) => !categoryFilter || categoryFilterRegExp.test(option.categoryName),
  );
  const categoryOnChange = (category: string): void => {
    setSelectedCategory(category);

    if (isGfcPublish) {
      // GFC: only update the active zone's categoryId and name (one zone == one Drop Zone)
      const categoryName = productTaxonomy?.categoryList?.find((c) => c.categoryId === category)?.categoryName;
      setZoneCreationProperties((prev) => {
        const newMap = new Map(prev);
        const activeZone = newMap.get(activeZoneIdx);
        if (activeZone) {
          newMap.set(activeZoneIdx, {
            ...activeZone,
            zoneName: categoryName || category,
            categoryId: category,
          });
        }
        return newMap;
      });
    } else {
      // Fresh/WFM: update all zones' categoryId
      zoneCreationProperties.forEach((_, zoneIdx) => {
        updateCategoryId(zoneIdx, category);
      });
    }
  };

  // GFC: Add a new Drop Zone card
  const handleAddDropZone = () => {
    const nextIdx = Math.max(...Array.from(zoneCreationProperties.keys())) + 1;
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      newMap.set(nextIdx, {
        zoneIdx: nextIdx,
        zoneName: '',
        productGroup: selectedProductGroup,
        categoryId: '',
        subcategoryIds: [],
        subcategoryNames: [],
        fixtureIds: [],
        fixtureModels: new Map(),
        facings: 1,
        pickPaths: [],
      });
      return newMap;
    });
    setActiveZoneIdx(nextIdx);
  };

  // GFC: assign a category to a specific Drop Zone card.
  const handleZoneCategoryChange = (zoneIdx: number, categoryId: string) => {
    setActiveZoneIdx(zoneIdx);
    setSelectedCategory(categoryId);
    const categoryName = productTaxonomy?.categoryList?.find((c) => c.categoryId === categoryId)?.categoryName;
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      const zone = newMap.get(zoneIdx);
      if (zone) {
        newMap.set(zoneIdx, {
          ...zone,
          zoneName: categoryName || categoryId,
          categoryId,
        });
      }
      return newMap;
    });
  };

  // GFC: remove a Drop Zone card. Re-points the active card if the removed one was active.
  const handleRemoveDropZone = (zoneIdx: number) => {
    const removedFixtures = zoneCreationProperties.get(zoneIdx)?.fixtureIds ?? [];
    setZoneCreationProperties((prev) => {
      const newMap = new Map(prev);
      newMap.delete(zoneIdx);
      return newMap;
    });
    setSelectedFixtures((prev) => prev.filter((id) => !removedFixtures.includes(id)));
    if (activeZoneIdx === zoneIdx) {
      const remaining = Array.from(zoneCreationProperties.keys()).filter((k) => k !== zoneIdx);
      if (remaining.length > 0) setActiveZoneIdx(remaining[0]);
    }
  };

  const onClosingSuccessfulModal = () => {
    if (switchButtonValue === SwitchButtonValue.CopyToNewMap && copyToNewDate) {
      setDate(copyToNewDate);
    }
    setSwitchButtonValue(SwitchButtonValue.ViewZoneMap);
  };

  const updateZoneCreationPropertiesForCopyToNew = () => {
    const {
      zoneCreationProperties: copyProps,
      selectedFixtures: copyFixtures,
      derivedCategoryId,
      derivedProductGroup,
    } = buildCopyToNewProperties({
      macroplanogramList,
      agileFixtures,
      selectedDepartment,
      selectedProductGroup,
      selectedCategory,
      viewAllocations,
      nextGenEnabled,
      isGetFloorplanSuccess,
      productTaxonomy,
    });
    if (derivedCategoryId && derivedCategoryId !== selectedCategory) {
      setSelectedCategory(derivedCategoryId);
    }
    if (derivedProductGroup && derivedProductGroup !== selectedProductGroup) {
      setSelectedProductGroup(derivedProductGroup);
    }
    setSelectedFixtures(copyFixtures);
    setZoneCreationProperties(copyProps);
  };

  const onClickCopyToNew = () => {
    setCopyToNewDate(date);
    updateZoneCreationPropertiesForCopyToNew();
  };

  const handleSwitchButtonChange = (newButtonValue: SwitchButtonValue | undefined) => {
    if (newButtonValue === SwitchButtonValue.CopyToNewMap) {
      onClickCopyToNew();
    } else if (newButtonValue === SwitchButtonValue.CreateZoneMap) {
      setZoneCreationProperties(initialZoneCreationPropertiesMap);
      setSelectedFixtures([]);
      setSelectedCategory('');
      setSelectedProductGroup('');
    }
    setSwitchButtonValue(newButtonValue);
  };

  const handleButtonGroupChange = (newButtonValue: SwitchButtonValue | undefined) => {
    // Check if the button value is changing
    if (
      (switchButtonValue === SwitchButtonValue.CopyToNewMap || switchButtonValue === SwitchButtonValue.CreateZoneMap) &&
      newButtonValue !== switchButtonValue &&
      date &&
      store
    ) {
      setNewSwitchButtonValue(newButtonValue);
      setStoreOrModeChangeConfirmationModal({
        open: true,
        showStoreWarning: false,
      });
    } else if (switchButtonValue === SwitchButtonValue.ViewZoneMap) {
      handleSwitchButtonChange(newButtonValue);
    } else {
      if (newButtonValue === SwitchButtonValue.CopyToNewMap) {
        onClickCopyToNew();
      }
      setSwitchButtonValue(newButtonValue);
    }
  };

  const onConfirmSwitchButton = () => {
    handleSwitchButtonChange(newSwitchButtonValue);
    setStoreOrModeChangeConfirmationModal({
      open: false,
      showStoreWarning: false,
    });
  };

  const onClickStoreSelect = (newStoreValue: string) => {
    if (
      (switchButtonValue === SwitchButtonValue.CopyToNewMap ||
        switchButtonValue === SwitchButtonValue.CreateZoneMap ||
        switchButtonValue === SwitchButtonValue.CopyToMultipleStores) &&
      store &&
      newStoreValue !== store
    ) {
      setNewStore(newStoreValue);
      setStoreOrModeChangeConfirmationModal({
        open: true,
        showStoreWarning: true,
      });
    } else {
      setStore(newStoreValue);
    }
  };

  useEffect(() => {
    setSearchParams(
      NavigationHelper.createSearchParams(
        {
          storeId: store,
          date,
        },
        urlParams.demo,
      ),
    );
    setUrlParameter({
      storeId: store,
      date,
      zoneId: selectedZoneId,
      demo: urlParams.demo,
    });
  }, [date, store, setSearchParams, setUrlParameter, selectedZoneId, urlParams.demo]);

  useEffect(() => {
    if (
      isGetZoneDataSuccess &&
      isGetFloorplanSuccess &&
      switchButtonValue === SwitchButtonValue.CopyToNewMap &&
      !storeOrModeChangeConfirmationModal.open
    ) {
      updateZoneCreationPropertiesForCopyToNew();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGetZoneDataSuccess, switchButtonValue, selectedDepartment, store, date, copyToNewDate, isGetFloorplanSuccess]);

  useEffect(() => {
    if (productGroupList.length > 0 && !selectedProductGroup) {
      setSelectedProductGroup(productGroupList[0].name);
    }
  }, [productGroupList, selectedProductGroup]);

  useEffect(() => {
    if (!selectedProductGroup) {
      // Clear category when product group is cleared
      setSelectedCategory('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCategoryOptions, selectedProductGroup]);

  return (
    <>
      <Toaster toasts={toasts} onCloseToast={onCloseToast}>
        {(toast) => (
          <Alert toast {...toast}>
            {toast.children}
          </Alert>
        )}
      </Toaster>
      <Column width="100%" height="100%">
        <ZoneCreationMenu
          switchButtonValue={switchButtonValue}
          onClickStoreSelect={onClickStoreSelect}
          handleButtonGroupChange={handleButtonGroupChange}
          store={store}
          date={date}
          setDate={setDate}
          showFloorplanPicker={floorplanPickerEnabled}
          floorplans={storeFloorplans}
          isFloorplansLoading={isListFloorplansLoading}
        />
        {!(store && date && switchButtonValue !== undefined) && (
          <Column alignmentHorizontal="center" alignmentVertical="center" height="100%" heights={['10%', '10%', '0%']}>
            <Heading level={1} type="h600">
              Zone Map Dashboard
            </Heading>
            <Heading level={1} type="h200">
              Please select a store, date, and button to get started
            </Heading>
          </Column>
        )}
        {date &&
          store &&
          switchButtonValue === SwitchButtonValue.ViewZoneMap &&
          (isGetFloorplanLoading ? (
            <div className="loader-container">
              <Loader type="circular" size="large" />
            </div>
          ) : (
            fpData.length > 0 &&
            isGetZoneDataSuccess && (
              <>
                <Row width="100%" widths={['100%']} height="100%">
                  <ViewZoneComponent
                    floorplanData={filteredFpData}
                    enableClick
                    fixtureIdToZoneDataMap={fixtureIdToZoneDataMapForView}
                    fixtureOrderingMap={
                      pickPathEditing.isEditingPickPath ? pickPathEditing.editingOrderMap : fixtureOrderingMap
                    }
                    pickPathLayers={
                      pickPathEditing.isEditingPickPath ? pickPathEditing.editingPickPathLayers : viewPickPathLayers
                    }
                    fixtureAssignments={viewFixtureAssignments}
                    allocations={viewAllocations}
                    availableDepartments={config.zoneMapBehavior.supportsDepartmentFilter ? availableDepartments : []}
                    selectedDepartment={selectedDepartment}
                    onDepartmentChange={
                      config.zoneMapBehavior.supportsDepartmentFilter ? setSelectedDepartment : undefined
                    }
                    onCopyToNew={() => setSwitchButtonValue(SwitchButtonValue.CopyToNewMap)}
                    onCopyToMultipleStores={() => setSwitchButtonValue(SwitchButtonValue.CopyToMultipleStores)}
                    onEditPickPath={isGfcPublish ? pickPathEditing.startEditingPickPath : undefined}
                    isEditingPickPath={pickPathEditing.isEditingPickPath}
                    onSavePickPath={pickPathEditing.savePickPath}
                    onCancelPickPath={pickPathEditing.cancelEditingPickPath}
                    onUndoPickPathFixture={pickPathEditing.undoLastFixture}
                    onClearPickPath={pickPathEditing.clearPath}
                    isSavingPickPath={pickPathEditing.isSavingPickPath}
                    editingPickPathCount={pickPathEditing.sequencedCount}
                    pickPathStartNumber={pickPathEditing.startNumber}
                    pickPathStepIncrement={pickPathEditing.stepIncrement}
                    onPickPathStartChange={pickPathEditing.updateStartNumber}
                    onPickPathStepChange={pickPathEditing.updateStepIncrement}
                    onEditPointerDown={pickPathEditing.onPointerDownFixture}
                    onEditPointerOver={pickPathEditing.onPointerOverFixture}
                    onEditPointerUp={pickPathEditing.onPointerUp}
                  />
                </Row>
              </>
            )
          ))}
        {(switchButtonValue === SwitchButtonValue.CreateZoneMap ||
          switchButtonValue === SwitchButtonValue.CopyToNewMap) &&
          date &&
          store && (
            <Row height="100%" maxHeight="100%" overflowY="hidden" widths={['22%', '78%']}>
              <Column spacingInset="400" spacing="400" minWidth={400} height="100%" overflowY="scroll">
                <Heading level={4}>Create new zone map</Heading>
                {switchButtonValue === SwitchButtonValue.CopyToNewMap && (
                  <CustomDatePicker
                    value={copyToNewDate}
                    onChange={setCopyToNewDate}
                    label="New Live Date"
                    error={!copyToNewDate}
                    disabledDates={(d) => d < new Date().toISOString().split('T')[0]}
                  />
                )}
                <Select
                  label={config.labels.productGroup}
                  value={selectedProductGroup}
                  onChange={handleProductGroupChange}
                  searchQuery={productGroupFilter}
                  onSearch={setProductGroupFilter}
                  placeholder={`Choose a ${config.labels.productGroup.toLowerCase()}...`}
                  width={300}
                >
                  {filteredProductGroupOptions.map((option: ProductGroupOption) => (
                    <SelectOption label={option.label} value={option.value} key={option.value} />
                  ))}
                  {!filteredProductGroupOptions.length ? (
                    <Column alignmentVertical="center" spacing="300" spacingInset="600">
                      <Text alignment="center">No results</Text>
                    </Column>
                  ) : null}
                </Select>
                {!isGfcPublish && (
                  <Select
                    label={config.labels.category}
                    value={selectedCategory}
                    onChange={categoryOnChange}
                    searchQuery={categoryFilter}
                    onSearch={setCategoryFilter}
                    placeholder={`Choose a ${config.labels.category.toLowerCase()}...`}
                    width={300}
                  >
                    {filteredCategoryOptions?.map((option) => (
                      <SelectOption label={option.categoryName} value={option.categoryId} key={option.categoryId} />
                    ))}
                    {!filteredCategoryOptions || filteredCategoryOptions.length === 0 ? (
                      <Column alignmentVertical="center" spacing="300" spacingInset="600">
                        <Text alignment="center">No results</Text>
                      </Column>
                    ) : null}
                  </Select>
                )}
                {Array.from(zoneCreationProperties.entries()).map(
                  (zoneCreationEntry: [number, ZoneCreationProperties]) => {
                    const zoneIdx = zoneCreationEntry[0];
                    const zoneProps = zoneCreationEntry[1];
                    // GFC: the picker on each card filters out categories already
                    // taken by other cards.
                    const usedCategoryIds = new Set(
                      Array.from(zoneCreationProperties.values())
                        .filter((z) => z.zoneIdx !== zoneIdx)
                        .map((z) => z.categoryId)
                        .filter(Boolean),
                    );
                    const cardCategoryOptions = (filteredCategoryOptions ?? []).filter(
                      (opt) => !usedCategoryIds.has(opt.categoryId),
                    );
                    return (
                      <Column spacing="300" key={zoneIdx}>
                        <Divider />
                        <ZoneCreationComponent
                          zoneIdx={zoneIdx}
                          fixtures={agileFixtures}
                          selectedFixtures={selectedFixtures}
                          zoneCreationProps={zoneProps}
                          updateSubcategories={updateSubcategories}
                          updateAllocations={updateAllocations}
                          productTaxonomy={productTaxonomy}
                          isActive={activeZoneIdx === zoneIdx}
                          onActivate={() => {
                            setActiveZoneIdx(zoneIdx);
                            if (isGfcPublish && zoneProps.categoryId) {
                              setSelectedCategory(zoneProps.categoryId);
                            }
                          }}
                          selectedDepartment={selectedDepartment}
                          categoryOptions={cardCategoryOptions}
                          onCategoryChange={(categoryId) => handleZoneCategoryChange(zoneIdx, categoryId)}
                          onRemove={() => handleRemoveDropZone(zoneIdx)}
                          canRemove={isGfcPublish && zoneCreationProperties.size > 1}
                        />
                      </Column>
                    );
                  },
                )}
                {isGfcPublish && (
                  <Button onClick={handleAddDropZone} type="secondary" size="small">
                    + Add Drop Zone
                  </Button>
                )}
                {(isGfcPublish ||
                  Array.from(zoneCreationProperties.values()).every(
                    (zone) => zone.allocations && validateAllocations(zone.allocations).sum === 100,
                  )) && (
                  <Row width={300} widths={[300]}>
                    <Button onClick={() => handleZoneCreationSubmit(setOpenConfirmationModal)}>Save Zone Map</Button>
                  </Row>
                )}
                <Modal
                  title="Zone Creation Validation Error"
                  open={zoneCreationValidationErrors.length > 0}
                  onClose={() => setZoneCreationValidationErrors([])}
                >
                  {zoneCreationValidationErrors.map((error) => (
                    <Text>{error}</Text>
                  ))}
                </Modal>
              </Column>
              <Column height="100%">
                {isGetFloorplanLoading ? (
                  <div className="loader-container">
                    <Loader type="circular" size="large" />
                  </div>
                ) : (
                  fpData.length > 0 && (
                    <ViewZoneComponent
                      floorplanData={filteredFpData}
                      enableClick={false}
                      fixtureIdToZoneDataMap={fixtureIdToEditingZoneDataMap}
                      fixtureOrderingMap={displayFixtureOrderingMap}
                      fixtureAssignments={fixtureAssignments}
                      allocations={zoneCreationProperties.get(activeZoneIdx)?.allocations}
                      dropZoneLegendEntries={dropZoneLegendEntries}
                      selectionEnabled={isSelectionEnabled}
                      activeZoneIdx={activeZoneIdx}
                      zones={zoneCreationProperties}
                      onFixtureSelect={handleFixtureClickWithFeedback}
                      onMarqueeAssign={handleMarqueeAssign}
                      onActiveZoneChange={setActiveZoneIdx}
                      isDrawingPickPath={pickPathDrawing.isDrawing}
                      pickPathLayers={
                        pickPathDrawing.isDrawing && pickPathDrawing.pickPathFeatureCollection.features.length > 0
                          ? pickPathDrawing.pickPathFeatureCollection
                          : isGfcPublish
                            ? undefined
                            : pickPathDrawing.pickPathFeatureCollection.features.length > 0
                              ? pickPathDrawing.pickPathFeatureCollection
                              : viewPickPathLayers
                      }
                      onStartDrawing={
                        config.features.zoneMap?.productFlow &&
                        (!config.zoneMapBehavior.requireDepartmentForProductFlow || selectedDepartment)
                          ? pickPathDrawing.startDrawing
                          : undefined
                      }
                      onStopDrawing={pickPathDrawing.stopDrawing}
                      onFinishPath={pickPathDrawing.finishPath}
                      onSubmitSequence={handleSubmitSequence}
                      hasPickPaths={hasPickPaths}
                      isSubmittingSequence={isSubmittingSequence}
                      hasExistingSequence={fixtureOrderingMap.size > 0}
                      availableDepartments={config.zoneMapBehavior.supportsDepartmentFilter ? availableDepartments : []}
                      selectedDepartment={selectedDepartment}
                      onDepartmentChange={
                        config.zoneMapBehavior.supportsDepartmentFilter ? setSelectedDepartment : undefined
                      }
                      onFixturePointerOver={pickPathDrawing.handleFixturePointerOver}
                      onPointerDownForPath={pickPathDrawing.onPointerDown}
                      onUndoWaypoint={pickPathDrawing.undoLastWaypoint}
                      canUndo={(pickPathDrawing.inProgressPath?.waypoints.length ?? 0) > 0}
                      displayModeOverride={
                        pickPathDrawing.isDrawing ? 'PICK_SEQUENCE' : isGfcPublish ? 'FIXTURE_ID' : undefined
                      }
                      totalFixtureCount={agileFixtures.length}
                      sequencedFixtureCount={editingFixtureOrderingMap.size}
                      showCreateNumbering={isGfcPublish}
                      createStartNumber={createStartNumber}
                      createStepIncrement={createStepIncrement}
                      onCreateStartNumberChange={updateCreateStartNumber}
                      onCreateStepIncrementChange={updateCreateStepIncrement}
                    />
                  )
                )}
                <ZoneViewerModalComponent
                  openConfirmationModal={openConfirmationModal}
                  setOpenConfirmationModal={setOpenConfirmationModal}
                  publishZoneErrorMsg={publishZoneErrorMsg}
                  setPublishZoneErrorMsg={setPublishZoneErrorMsg}
                  publishZoneSuccessful={publishZoneSuccessful}
                  setPublishZoneSuccessful={setPublishZoneSuccessful}
                  onPublishButton={onPublishButton}
                  store={store}
                  timeStamp={timeStamp}
                  copyToNewTimestamp={copyToNewTimestamp}
                  zonesToUpdate={zoneCreationProperties}
                  isLoading={isLoadingPublishZone}
                  onClosingSuccessfulModal={onClosingSuccessfulModal}
                  switchButtonValue={switchButtonValue}
                  fixtureAssignments={fixtureAssignments}
                  macroplanogramNameInput={macroplanogramNameInput}
                  onMacroplanogramNameChange={setMacroplanogramNameInput}
                />
              </Column>
            </Row>
          )}
        {switchButtonValue === SwitchButtonValue.CopyToMultipleStores && date && store && (
          <MultiStoreCopy
            sourceAllocations={viewAllocations}
            sourceStoreId={store}
            selectedDepartment={selectedDepartment}
            date={date}
            macroplanogramName={
              macroplanogramList?.[0]?.macroplanogramName ||
              macroplanogramNameInput.trim() ||
              selectedDepartment ||
              'Zone Map'
            }
            onExit={() => setSwitchButtonValue(SwitchButtonValue.ViewZoneMap)}
          />
        )}
      </Column>
      {storeOrModeChangeConfirmationModal.open && store && date && (
        <StoreOrModeChangeConfirmationModal
          newStore={newStore}
          switchButtonValue={switchButtonValue}
          setStore={setStore}
          setNewStore={setNewStore}
          setSwitchButtonValue={setSwitchButtonValue}
          setStoreOrModeChangeConfirmationModal={setStoreOrModeChangeConfirmationModal}
          storeChangeConfirmationModal={storeOrModeChangeConfirmationModal}
          setZoneCreationProperties={setZoneCreationProperties}
          initialZoneCreationPropertiesMap={initialZoneCreationPropertiesMap}
          onConfirmSwitchButton={onConfirmSwitchButton}
        />
      )}
      {reassignmentConfirmation && (
        <Modal title="Reassign Fixture" open={reassignmentConfirmation.open} onClose={handleCancelReassignment}>
          <Column spacing="400">
            <Text>
              Fixture {reassignmentConfirmation.fixtureId} is currently assigned to{' '}
              {reassignmentConfirmation.sourceZoneName}. Reassign to {reassignmentConfirmation.targetZoneName}?
            </Text>
            <Row spacing="300">
              <Button onClick={handleConfirmReassignment}>Reassign</Button>
              <Button onClick={handleCancelReassignment} type="secondary">
                Cancel
              </Button>
            </Row>
          </Column>
        </Modal>
      )}
    </>
  );
};

export default ZoneViewer;
